import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Switch, Chip, CircularProgress,
  Snackbar, Alert, ToggleButtonGroup, ToggleButton, Slider, IconButton, InputAdornment, alpha,
} from '@mui/material';
import {
  Bolt as BoltIcon, Telegram as TelegramIcon, NotificationsActive as BellIcon,
  Link as LinkIcon, Schedule as ScheduleIcon, Add as AddIcon, Close as CloseIcon,
  Send as SendIcon, PlayArrow as PlayIcon, CheckCircle as CheckIcon,
  Visibility as EyeIcon, VisibilityOff as EyeOffIcon,
  DeleteSweep as DeleteSweepIcon, Whatshot as FireIcon,
  FavoriteBorder as LikeIcon, OpenInNew as ViewIcon, ChatBubbleOutline as ReplyIcon,
  Block as BlockIcon, ErrorOutline as ErrorIcon, Groups as PeopleIcon,
  ArrowUpward as UpIcon, ArrowDownward as DownIcon, FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import axios, { API } from '../api';
import { designTokens as t } from '../theme';

// Heure locale "HH:MM" du moment (sert d'heure de vérification par défaut).
function nowHHMM() {
  const n = new Date();
  return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
}

const DEFAULTS = {
  telegram: { enabled: false, botToken: '', chatId: '' },
  automation: {
    notifyMilestones: true,
    milestones: [50, 100, 150, 300],
    notifySuspended: true,
    noLinkRule: { enabled: false, threshold: 100, action: 'note', note: '⚠️ Ajouter un lien en bio', tag: 'à-monétiser', notify: true },
    autoDeleteBanned: { enabled: false, afterChecks: 3, afterHours: 24, notify: true },
    followerCheck: { mode: 'times', times: [], intervalHours: 6 },
    warmupNew: {
      enabled: false,
      afterHours: 24,
      durationDays: 3,
      options: {
        durationMin: 6, durationMax: 12,
        like: true, likeProb: 40, maxLikes: 6, maxLikesHi: 14,
        comment: false, commentProb: 10, maxComments: 1, maxCommentsHi: 3, comments: [],
        view: true, viewProb: 30, minDelaySec: 5, maxDelaySec: 15, concurrency: 3,
      },
    },
    viralBoost: {
      enabled: false,
      minLikes: 1000,
      minReplies: 0,
      perDay: 3,
      hourStart: 9,
      hourEnd: 22,
      dates: [],
    },
  },
};

// Slider de plage "min → max" étirable (tiré au hasard par compte côté serveur).
function Range({ label, value, onChange, min = 1, max = 999, unit = '', color = '#f97316' }) {
  const [lo, hi] = value;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
        <Typography sx={{ fontSize: '0.7rem', color: t.muted }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>{lo} – {hi}{unit}</Typography>
      </Box>
      <Slider size="small" value={[Number(lo) || min, Number(hi) || min]} min={min} max={max} disableSwap
        onChange={(_, v) => onChange(v)} valueLabelDisplay="auto" sx={{ color, mx: 0.5, width: 'calc(100% - 8px)' }} />
    </Box>
  );
}

// Slider simple (une valeur).
function Single({ label, value, onChange, min = 1, max = 100, unit = '', color = '#f97316' }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
        <Typography sx={{ fontSize: '0.7rem', color: t.muted }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>{value}{unit}</Typography>
      </Box>
      <Slider size="small" value={Number(value) || 0} min={min} max={max} step={1} marks={max <= 6}
        onChange={(_, v) => onChange(v)} valueLabelDisplay="auto" sx={{ color, mx: 0.5, width: 'calc(100% - 8px)' }} />
    </Box>
  );
}

// ── Carte de réglage : en-tête (icône + titre + statut/switch) + corps ───────────
function Card({ icon, color, title, desc, right, off, children, span }) {
  return (
    <Box sx={{ gridColumn: span ? { md: '1 / -1' } : 'auto', border: `1px solid ${t.border}`, borderRadius: 3, bgcolor: t.card,
      p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.75, transition: 'border-color .2s',
      '&:hover': { borderColor: t.borderStrong } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          bgcolor: alpha(color, 0.14), color }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
          {desc && <Typography sx={{ fontSize: '0.76rem', color: t.muted, mt: 0.3 }}>{desc}</Typography>}
        </Box>
        {right && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>{right}</Box>}
      </Box>
      {children && (
        <Box sx={{ opacity: off ? 0.4 : 1, pointerEvents: off ? 'none' : 'auto', transition: 'opacity .2s',
          pt: 0.5, borderTop: `1px solid ${alpha(t.border, 0.7)}` }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

// Petit badge de statut coloré.
function Tag({ label, color }) {
  return (
    <Chip size="small" label={label} sx={{ bgcolor: alpha(color, 0.15), color, border: `1px solid ${alpha(color, 0.35)}`,
      fontWeight: 700, fontSize: '0.68rem', height: 22, borderRadius: 1.5 }} />
  );
}

// Section title (uppercase discret).
function Section({ children, first }) {
  return (
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: t.mutedFaint, mb: 1.25, mt: first ? 0 : 3 }}>{children}</Typography>
  );
}

// ── Journal live de la vérification des abonnés ──────────────────────────────────
function flAgo(ts) {
  const s = Math.max(0, Math.floor(Date.now() / 1000) - (ts || 0));
  if (s < 5) return "à l'instant";
  if (s < 60) return `il y a ${s}s`;
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  return `il y a ${Math.floor(s / 3600)} h`;
}

const FL_STATUS = {
  ok: { label: 'OK', color: t.success },
  banned: { label: 'Suspendu', color: t.error },
  expired: { label: 'Session expirée', color: '#fb923c' },
  challenge: { label: 'Challenge', color: '#eab308' },
  error: { label: 'Erreur', color: '#f59e0b' },
  checking: { label: 'Vérification…', color: t.primary },
};

function FollowerLogRow({ ev }) {
  const st = FL_STATUS[ev.status] || FL_STATUS.checking;
  const icon = ev.status === 'ok' ? <CheckIcon sx={{ fontSize: 15 }} />
    : ev.status === 'banned' ? <BlockIcon sx={{ fontSize: 15 }} />
    : ev.status === 'expired' || ev.status === 'challenge' ? <ErrorIcon sx={{ fontSize: 15 }} />
    : ev.status === 'error' ? <ErrorIcon sx={{ fontSize: 15 }} />
    : <CircularProgress size={13} thickness={6} sx={{ color: t.primary }} />;
  const delta = ev.delta || 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, px: 1.25, borderRadius: 2,
      bgcolor: ev.status === 'checking' ? alpha(t.primary, 0.06) : 'transparent',
      borderBottom: `1px solid ${alpha(t.border, 0.5)}`,
      animation: 'flIn .35s ease', '@keyframes flIn': { from: { opacity: 0, transform: 'translateY(-4px)' }, to: { opacity: 1, transform: 'none' } } }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(st.color, 0.14), color: st.color }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.83rem', fontWeight: 600 }}>@{ev.username || ev.account}</Typography>
        <Typography noWrap sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>
          {ev.status === 'checking' ? 'Récupération des abonnés…'
            : ev.status === 'banned' ? (ev.message || 'compte suspendu (confirmé)')
            : ev.status === 'expired' ? (ev.message || 'session expirée — reconnexion nécessaire')
            : ev.status === 'challenge' ? (ev.message || 'vérification Instagram requise')
            : ev.status === 'error' ? (ev.message || 'erreur')
            : flAgo(ev.at)}
        </Typography>
      </Box>
      {(ev.status === 'ok' || (ev.status !== 'checking' && ev.followers > 0)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
          <PeopleIcon sx={{ fontSize: 14, color: t.mutedFaint }} />
          <Typography sx={{ fontSize: '0.83rem', fontWeight: 700 }}>{(ev.followers || 0).toLocaleString('fr-FR')}</Typography>
        </Box>
      )}
      {ev.status === 'ok' && delta !== 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, flexShrink: 0,
          color: delta > 0 ? t.success : t.error, fontWeight: 700, fontSize: '0.76rem' }}>
          {delta > 0 ? <UpIcon sx={{ fontSize: 13 }} /> : <DownIcon sx={{ fontSize: 13 }} />}
          {Math.abs(delta)}
        </Box>
      )}
      <Box sx={{ flexShrink: 0, px: 1, py: 0.3, borderRadius: 1.5, minWidth: 74, textAlign: 'center',
        bgcolor: alpha(st.color, 0.14), color: st.color, border: `1px solid ${alpha(st.color, 0.3)}`,
        fontWeight: 700, fontSize: '0.68rem' }}>{st.label}</Box>
    </Box>
  );
}

function FollowerLog({ log, onRefresh }) {
  const events = (log.events || []).filter((e) => e.status !== 'run_start' && e.status !== 'run_end');
  const recent = [...events].reverse(); // plus récents en haut
  const running = log.running;
  const total = log.total || 0;
  const done = Math.min(log.done || 0, total || log.done || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const okCount = events.filter((e) => e.status === 'ok').length;
  const banCount = events.filter((e) => e.status === 'banned').length;
  const expCount = events.filter((e) => e.status === 'expired' || e.status === 'challenge').length;
  const errCount = events.filter((e) => e.status === 'error').length;

  return (
    <Box sx={{ gridColumn: { md: '1 / -1' }, border: `1px solid ${running ? alpha(t.primary, 0.5) : t.border}`, borderRadius: 3,
      bgcolor: t.card, p: 2.25, transition: 'border-color .3s' }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          bgcolor: alpha(t.primary, 0.14), color: t.primary }}><PeopleIcon fontSize="small" /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Activité en direct</Typography>
            {running && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.2, borderRadius: 5,
                bgcolor: alpha(t.error, 0.14), border: `1px solid ${alpha(t.error, 0.35)}` }}>
                <DotIcon sx={{ fontSize: 9, color: t.error, animation: 'flPulse 1.2s ease-in-out infinite',
                  '@keyframes flPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } } }} />
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: t.error, letterSpacing: '0.04em' }}>EN DIRECT</Typography>
              </Box>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.76rem', color: t.muted, mt: 0.2 }}>
            {running ? `Vérification en cours — ${done}/${total} compte(s)`
              : events.length ? 'Dernière vérification' : 'Aucune vérification récente'}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onRefresh} sx={{ flexShrink: 0 }}>Rafraîchir</Button>
      </Box>

      {/* Barre de progression */}
      {running && (
        <Box sx={{ height: 6, borderRadius: 3, bgcolor: alpha(t.primary, 0.12), overflow: 'hidden', mb: 1.5 }}>
          <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 3, bgcolor: t.primary, transition: 'width .5s ease' }} />
        </Box>
      )}

      {/* Compteurs de synthèse */}
      {events.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Tag label={`${okCount} OK`} color={t.success} />
          {banCount > 0 && <Tag label={`${banCount} suspendu${banCount > 1 ? 's' : ''}`} color={t.error} />}
          {expCount > 0 && <Tag label={`${expCount} à reconnecter`} color="#fb923c" />}
          {errCount > 0 && <Tag label={`${errCount} erreur${errCount > 1 ? 's' : ''}`} color="#f59e0b" />}
        </Box>
      )}

      {/* Liste des événements */}
      {recent.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.82rem', color: t.mutedFaint }}>
            Lance une vérification pour voir l'activité des comptes en temps réel.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
          {recent.map((ev, i) => <FollowerLogRow key={`${ev.account}-${ev.at}-${i}`} ev={ev} />)}
        </Box>
      )}
    </Box>
  );
}

// ── Programmation de warm-ups sur un calendrier (X jours) ────────────────────────
const WU_MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const WU_WD = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
const wuKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function wuFmtRunAt(ts) {
  const d = new Date((ts || 0) * 1000);
  return d.toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Programmateur : sélection de comptes + jours (calendrier + raccourcis X jours) +
// heure → génère les timestamps et appelle onSchedule. State local (autonome).
function WarmupScheduler({ accounts, schedules, onSchedule, onCancel, busy }) {
  const [sel, setSel] = useState(new Set());
  const [dates, setDates] = useState(new Set());
  const [time, setTime] = useState('10:00');
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [q, setQ] = useState('');

  const visible = accounts.filter((a) => {
    const s = q.trim().toLowerCase();
    return !s || (a.username || a.name || '').toLowerCase().includes(s);
  });
  const allSel = visible.length > 0 && visible.every((a) => sel.has(a.name));
  const toggleOne = (name) => setSel((p) => { const n = new Set(p); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const toggleAll = () => setSel((p) => { const n = new Set(p); allSel ? visible.forEach((a) => n.delete(a.name)) : visible.forEach((a) => n.add(a.name)); return n; });
  const toggleDate = (d) => setDates((p) => { const k = wuKey(d); const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const addNextDays = (nb) => setDates((p) => { const n = new Set(p); for (let i = 0; i < nb; i += 1) { const dd = new Date(); dd.setDate(dd.getDate() + i); n.add(wuKey(dd)); } return n; });

  const calDays = (() => {
    const y = month.getFullYear(); const m = month.getMonth();
    const startWd = (new Date(y, m, 1).getDay() + 6) % 7;
    const nDays = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startWd; i += 1) arr.push(null);
    for (let d = 1; d <= nDays; d += 1) arr.push(new Date(y, m, d));
    return arr;
  })();

  const submit = () => {
    const [hh, mm] = (time || '10:00').split(':').map((x) => parseInt(x, 10) || 0);
    const now = Math.floor(Date.now() / 1000);
    const runAts = [...dates]
      .map((k) => { const [y, mo, d] = k.split('-').map(Number); return Math.floor(new Date(y, mo - 1, d, hh, mm, 0).getTime() / 1000); })
      .filter((ts) => ts > now - 60);
    onSchedule([...sel], runAts, () => { setDates(new Set()); });
  };

  return (
    <Box sx={{ gridColumn: { md: '1 / -1' }, border: `1px solid ${t.border}`, borderRadius: 3, bgcolor: t.card, p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          bgcolor: alpha('#f97316', 0.14), color: '#f97316' }}><ScheduleIcon fontSize="small" /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Programmer des warm-ups</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: t.muted, mt: 0.2 }}>Chauffe les comptes choisis sur X jours (mêmes réglages que ci-dessus).</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
        {/* comptes */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField size="small" fullWidth placeholder="Rechercher un compte…" value={q} onChange={(e) => setQ(e.target.value)} />
            <Button size="small" onClick={toggleAll} sx={{ flexShrink: 0, color: '#fdba74' }}>{allSel ? 'Aucun' : 'Tous'}</Button>
          </Box>
          <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2, maxHeight: 260, overflowY: 'auto' }}>
            {visible.length === 0 ? (
              <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint, p: 2, textAlign: 'center' }}>Aucun compte.</Typography>
            ) : visible.map((a) => (
              <Box key={a.name} onClick={() => toggleOne(a.name)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.85, cursor: 'pointer',
                  borderBottom: `1px solid ${alpha(t.border, 0.5)}`, bgcolor: sel.has(a.name) ? alpha('#f97316', 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha('#fff', 0.03) } }}>
                <Box sx={{ width: 16, height: 16, borderRadius: 0.75, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1.5px solid ${sel.has(a.name) ? '#f97316' : t.borderStrong}`, bgcolor: sel.has(a.name) ? '#f97316' : 'transparent' }}>
                  {sel.has(a.name) && <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />}
                </Box>
                <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 500 }}>@{a.username || a.name}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mt: 0.75 }}>{sel.size} compte(s) sélectionné(s)</Typography>
        </Box>

        {/* calendrier */}
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField size="small" type="time" label="Heure" value={time} onChange={(e) => setTime(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
            <Box sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2, p: 1.5, bgcolor: t.background }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <IconButton size="small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</IconButton>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{WU_MONTHS[month.getMonth()]} {month.getFullYear()}</Typography>
              <IconButton size="small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {WU_WD.map((w, i) => <Typography key={i} sx={{ textAlign: 'center', fontSize: '0.66rem', color: t.mutedFaint, py: 0.5 }}>{w}</Typography>)}
              {calDays.map((d, i) => {
                if (!d) return <Box key={i} />;
                const k = wuKey(d);
                const isSel = dates.has(k);
                const t0 = new Date(); t0.setHours(0, 0, 0, 0);
                const isPast = d < t0;
                const isToday = k === wuKey(new Date());
                return (
                  <Box key={i} onClick={() => !isPast && toggleDate(d)}
                    sx={{ textAlign: 'center', py: 0.55, borderRadius: 1.5, fontSize: '0.78rem', cursor: isPast ? 'not-allowed' : 'pointer',
                      color: isPast ? t.mutedFaint : isSel ? '#fff' : t.foreground, opacity: isPast ? 0.35 : 1,
                      bgcolor: isSel ? '#f97316' : 'transparent', fontWeight: isSel ? 700 : 500,
                      border: isToday && !isSel ? '1px solid #f97316' : '1px solid transparent',
                      '&:hover': isPast ? {} : { bgcolor: isSel ? '#ea580c' : alpha('#fff', 0.06) } }}>
                    {d.getDate()}
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
              {[7, 15, 30].map((nb) => (
                <Box key={nb} role="button" onClick={() => addNextDays(nb)}
                  sx={{ px: 1, py: 0.35, borderRadius: 1.5, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', border: `1px solid ${t.border}`, color: t.muted,
                    '&:hover': { borderColor: '#f97316', color: '#fdba74' } }}>{nb} prochains j</Box>
              ))}
              {dates.size > 0 && (
                <Box role="button" onClick={() => setDates(new Set())} sx={{ px: 1, py: 0.35, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', color: t.mutedFaint, '&:hover': { color: t.foreground } }}>Effacer</Box>
              )}
            </Box>
          </Box>
          {dates.size > 0 && (
            <Typography sx={{ fontSize: '0.74rem', color: '#fdba74', mt: 0.75 }}>{dates.size} date(s) · warm-up à <b>{time}</b> (heure locale).</Typography>
          )}
        </Box>
      </Box>

      {/* action */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={submit} disabled={busy || sel.size === 0 || dates.size === 0}
          startIcon={busy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ScheduleIcon />}
          sx={{ px: 2.5, background: 'linear-gradient(135deg, #fb923c, #ea580c)', color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #f97316, #c2410c)' },
            '&.Mui-disabled': { background: t.input, color: t.mutedFaint } }}>
          Programmer{dates.size > 0 && sel.size > 0 ? ` · ${sel.size}×${dates.size}` : ''}
        </Button>
      </Box>

      {/* à venir */}
      {schedules.length > 0 && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${alpha(t.border, 0.7)}` }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>À venir</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 160, overflowY: 'auto' }}>
            {schedules.map((s) => (
              <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.65, borderRadius: 1.5, bgcolor: alpha('#fff', 0.025), border: `1px solid ${t.border}` }}>
                <ScheduleIcon sx={{ fontSize: 15, color: '#fdba74' }} />
                <Typography sx={{ fontSize: '0.78rem', flex: 1 }}>{wuFmtRunAt(s.runAt)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>{s.accounts} compte(s)</Typography>
                <IconButton size="small" onClick={() => onCancel(s.id)} sx={{ color: t.mutedFaint }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Logs de warm-up des dernières 48h (mémoire serveur) ──────────────────────────
const WU_STATUS = {
  done: { label: 'Terminé', color: t.success },
  failed: { label: 'Échec', color: t.error },
  stopped: { label: 'Arrêté', color: t.mutedFaint },
};
function wuAgo(ts) {
  const s = Math.max(0, Math.floor(Date.now() / 1000) - (ts || 0));
  if (s < 60) return `il y a ${s}s`;
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

function WarmupLog({ entries, onRefresh }) {
  const doneN = entries.filter((e) => e.status === 'done').length;
  const failN = entries.filter((e) => e.status === 'failed').length;
  const likes = entries.reduce((a, e) => a + (e.likes || 0), 0);
  const comments = entries.reduce((a, e) => a + (e.comments || 0), 0);
  const views = entries.reduce((a, e) => a + (e.views || 0), 0);
  return (
    <Box sx={{ gridColumn: { md: '1 / -1' }, border: `1px solid ${t.border}`, borderRadius: 3, bgcolor: t.card, p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          bgcolor: alpha('#f97316', 0.14), color: '#f97316' }}><FireIcon fontSize="small" /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Logs warm-up · 48 h</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: t.muted, mt: 0.2 }}>
            {entries.length ? `${entries.length} compte(s) chauffé(s) sur les dernières 48 h` : 'Aucun warm-up sur les dernières 48 h'}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onRefresh} sx={{ flexShrink: 0 }}>Rafraîchir</Button>
      </Box>

      {entries.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Tag label={`${doneN} terminé${doneN > 1 ? 's' : ''}`} color={t.success} />
          {failN > 0 && <Tag label={`${failN} échec${failN > 1 ? 's' : ''}`} color={t.error} />}
          <Tag label={`♥ ${likes}`} color="#f472b6" />
          <Tag label={`💬 ${comments}`} color="#c4b5fd" />
          <Tag label={`👁 ${views}`} color="#93c5fd" />
        </Box>
      )}

      {entries.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.82rem', color: t.mutedFaint }}>Les warm-ups (manuels, auto ou programmés) apparaîtront ici.</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
          {entries.map((e, i) => {
            const st = WU_STATUS[e.status] || WU_STATUS.stopped;
            return (
              <Box key={`${e.account}-${e.at}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, px: 1.25, borderRadius: 2,
                borderBottom: `1px solid ${alpha(t.border, 0.5)}`, borderLeft: `3px solid ${st.color}`,
                animation: 'flIn .3s ease', '@keyframes flIn': { from: { opacity: 0, transform: 'translateY(-4px)' }, to: { opacity: 1, transform: 'none' } } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.83rem', fontWeight: 600 }}>@{e.username || e.account}</Typography>
                  <Typography noWrap sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>
                    {wuAgo(e.at)}{e.source ? ` · ${e.source}` : ''}{e.status === 'failed' && e.message ? ` · ${e.message}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.74rem', color: '#f472b6' }}>♥ {e.likes || 0}</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#c4b5fd' }}>💬 {e.comments || 0}</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#93c5fd' }}>👁 {e.views || 0}</Typography>
                </Box>
                <Box sx={{ flexShrink: 0, px: 1, py: 0.3, borderRadius: 1.5, minWidth: 68, textAlign: 'center',
                  bgcolor: alpha(st.color, 0.14), color: st.color, border: `1px solid ${alpha(st.color, 0.3)}`,
                  fontWeight: 700, fontSize: '0.68rem' }}>{st.label}</Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ── Viral Boost : republie un post qui a explosé, N fois/jour sur des dates choisies ──
function ViralBoostCard({ cfg, onChange, onScanNow }) {
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const dates = new Set(Array.isArray(cfg.dates) ? cfg.dates : []);
  const setField = (patch) => onChange({ ...cfg, ...patch });
  const toggleDate = (d) => {
    const k = wuKey(d); const n = new Set(dates);
    n.has(k) ? n.delete(k) : n.add(k);
    setField({ dates: [...n].sort() });
  };
  const addNextDays = (nb) => {
    const n = new Set(dates);
    for (let i = 0; i < nb; i += 1) { const dd = new Date(); dd.setDate(dd.getDate() + i); n.add(wuKey(dd)); }
    setField({ dates: [...n].sort() });
  };
  const calDays = (() => {
    const y = month.getFullYear(); const m = month.getMonth();
    const startWd = (new Date(y, m, 1).getDay() + 6) % 7;
    const nDays = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startWd; i += 1) arr.push(null);
    for (let d = 1; d <= nDays; d += 1) arr.push(new Date(y, m, d));
    return arr;
  })();
  const off = !cfg.enabled;

  return (
    <Box sx={{ gridColumn: { md: '1 / -1' }, border: `1px solid ${cfg.enabled ? alpha('#ef4444', 0.4) : t.border}`, borderRadius: 3,
      bgcolor: t.card, p: 2.25, opacity: off ? 0.85 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          bgcolor: alpha('#ef4444', 0.14), color: '#ef4444' }}><FireIcon fontSize="small" /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Republier les posts qui explosent</Typography>
          <Typography sx={{ fontSize: '0.76rem', color: t.muted, mt: 0.2 }}>
            Si un post dépasse un seuil de likes/commentaires, son texte est republié N fois/jour aux dates choisies.
          </Typography>
        </Box>
        <Chip size="small" label={cfg.enabled ? 'Actif' : 'Désactivé'} sx={{ bgcolor: alpha(cfg.enabled ? '#ef4444' : t.mutedFaint, 0.15), color: cfg.enabled ? '#fca5a5' : t.mutedFaint, fontWeight: 700 }} />
        <Switch checked={!!cfg.enabled} onChange={(e) => setField({ enabled: e.target.checked })} />
      </Box>

      {/* seuils de déclenchement */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2, px: 1.5, py: 1.25, borderRadius: 2,
        bgcolor: alpha('#ef4444', 0.07), border: `1px solid ${alpha('#ef4444', 0.22)}` }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#fca5a5' }}>Déclencher si un post atteint</Typography>
        <TextField size="small" type="number" value={cfg.minLikes ?? 0}
          onChange={(e) => setField({ minLikes: Math.max(0, parseInt(e.target.value, 10) || 0) })}
          InputProps={{ startAdornment: <InputAdornment position="start"><LikeIcon sx={{ fontSize: 15, color: '#f472b6' }} /></InputAdornment> }}
          sx={{ width: 120 }} inputProps={{ min: 0 }} />
        <Typography sx={{ fontSize: '0.85rem', color: '#fca5a5' }}>likes OU</Typography>
        <TextField size="small" type="number" value={cfg.minReplies ?? 0}
          onChange={(e) => setField({ minReplies: Math.max(0, parseInt(e.target.value, 10) || 0) })}
          InputProps={{ startAdornment: <InputAdornment position="start"><ReplyIcon sx={{ fontSize: 15, color: '#c4b5fd' }} /></InputAdornment> }}
          sx={{ width: 120 }} inputProps={{ min: 0 }} />
        <Typography sx={{ fontSize: '0.85rem', color: '#fca5a5' }}>commentaires. (mettre 0 = ignorer ce seuil)</Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
        {/* rythme */}
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            <TextField size="small" type="number" label="Reposts / jour" value={cfg.perDay ?? 3}
              onChange={(e) => setField({ perDay: Math.min(24, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
              InputLabelProps={{ shrink: true }} sx={{ width: 130 }} inputProps={{ min: 1, max: 24 }} />
            <TextField size="small" type="number" label="Heure début" value={cfg.hourStart ?? 9}
              onChange={(e) => setField({ hourStart: Math.min(23, Math.max(0, parseInt(e.target.value, 10) || 0)) })}
              InputLabelProps={{ shrink: true }} InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} sx={{ width: 120 }} inputProps={{ min: 0, max: 23 }} />
            <TextField size="small" type="number" label="Heure fin" value={cfg.hourEnd ?? 22}
              onChange={(e) => setField({ hourEnd: Math.min(24, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
              InputLabelProps={{ shrink: true }} InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} sx={{ width: 120 }} inputProps={{ min: 1, max: 24 }} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mb: 1 }}>
            Les reposts sont étalés à des heures aléatoires dans la plage. Tant qu'un compte est boosté, il ne publie QUE ces reposts (les posts normaux sont en pause) — retire le tag <b>🔥 Viral</b> dans Comptes pour arrêter.
          </Typography>
          <Button size="small" variant="outlined" onClick={onScanNow} startIcon={<FireIcon sx={{ fontSize: 16 }} />}
            sx={{ color: '#fca5a5', borderColor: alpha('#ef4444', 0.4) }}>Scanner les posts maintenant</Button>
        </Box>

        {/* calendrier des dates */}
        <Box>
          <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2, p: 1.5, bgcolor: t.background }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <IconButton size="small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</IconButton>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{WU_MONTHS[month.getMonth()]} {month.getFullYear()}</Typography>
              <IconButton size="small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {WU_WD.map((w, i) => <Typography key={i} sx={{ textAlign: 'center', fontSize: '0.66rem', color: t.mutedFaint, py: 0.5 }}>{w}</Typography>)}
              {calDays.map((d, i) => {
                if (!d) return <Box key={i} />;
                const k = wuKey(d);
                const isSel = dates.has(k);
                const t0 = new Date(); t0.setHours(0, 0, 0, 0);
                const isPast = d < t0;
                const isToday = k === wuKey(new Date());
                return (
                  <Box key={i} onClick={() => !isPast && toggleDate(d)}
                    sx={{ textAlign: 'center', py: 0.55, borderRadius: 1.5, fontSize: '0.78rem', cursor: isPast ? 'not-allowed' : 'pointer',
                      color: isPast ? t.mutedFaint : isSel ? '#fff' : t.foreground, opacity: isPast ? 0.35 : 1,
                      bgcolor: isSel ? '#ef4444' : 'transparent', fontWeight: isSel ? 700 : 500,
                      border: isToday && !isSel ? '1px solid #ef4444' : '1px solid transparent',
                      '&:hover': isPast ? {} : { bgcolor: isSel ? '#dc2626' : alpha('#fff', 0.06) } }}>
                    {d.getDate()}
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
              {[7, 15, 30].map((nb) => (
                <Box key={nb} role="button" onClick={() => addNextDays(nb)}
                  sx={{ px: 1, py: 0.35, borderRadius: 1.5, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', border: `1px solid ${t.border}`, color: t.muted,
                    '&:hover': { borderColor: '#ef4444', color: '#fca5a5' } }}>{nb} prochains j</Box>
              ))}
              {dates.size > 0 && (
                <Box role="button" onClick={() => setField({ dates: [] })} sx={{ px: 1, py: 0.35, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', color: t.mutedFaint, '&:hover': { color: t.foreground } }}>Effacer</Box>
              )}
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.74rem', color: dates.size ? '#fca5a5' : t.mutedFaint, mt: 0.75 }}>
            {dates.size ? `${dates.size} date(s) de republication.` : 'Choisis les jours où les reposts doivent partir.'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// Ligne label + contrôle.
function Row({ title, desc, children, last }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: last ? 'none' : `1px solid ${alpha(t.border, 0.5)}` }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{title}</Typography>
        {desc && <Typography sx={{ fontSize: '0.74rem', color: t.muted, mt: 0.1 }}>{desc}</Typography>}
      </Box>
      <Box sx={{ flexShrink: 0 }}>{children}</Box>
    </Box>
  );
}

function ChipEditor({ values, onChange, placeholder, format = (v) => v, parse = (v) => v, type = 'text', color = t.primary }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = parse(input.trim());
    if (v === '' || v === null || v === undefined) return;
    if (values.some((x) => String(x) === String(v))) { setInput(''); return; }
    onChange([...values, v]);
    setInput('');
  };
  return (
    <Box>
      {values.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
          {values.map((v) => (
            <Chip key={String(v)} label={format(v)} size="small" onDelete={() => onChange(values.filter((x) => x !== v))}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{ bgcolor: alpha(color, 0.14), color: '#c7d2fe', fontWeight: 600, border: `1px solid ${alpha(color, 0.3)}` }} />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField size="small" type={type} value={input} placeholder={placeholder}
          onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          sx={{ width: 160 }} />
        <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={add}>Ajouter</Button>
      </Box>
    </Box>
  );
}

function SaveStatus({ state }) {
  if (state === 'saving') return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: t.muted }}><CircularProgress size={13} /><Typography sx={{ fontSize: '0.78rem' }}>Enregistrement…</Typography></Box>;
  if (state === 'error') return <Typography sx={{ fontSize: '0.78rem', color: '#fca5a5', fontWeight: 600 }}>Erreur d'enregistrement</Typography>;
  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#86efac' }}><CheckIcon sx={{ fontSize: 15 }} /><Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Enregistré</Typography></Box>;
}

export default function Automation() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('saved');
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [flog, setFlog] = useState({ running: false, total: 0, done: 0, events: [] });
  const [wuAccounts, setWuAccounts] = useState([]);
  const [wuSchedules, setWuSchedules] = useState([]);
  const [wuHist, setWuHist] = useState([]);
  const [wuBusy, setWuBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const lastSaved = useRef('');
  const [defaultHour] = useState(nowHHMM);

  const tg = settings.telegram;
  const au = settings.automation;
  const rule = au.noLinkRule;
  const del = au.autoDeleteBanned;
  const fc = au.followerCheck;

  const setTg = (patch) => setSettings((s) => ({ ...s, telegram: { ...s.telegram, ...patch } }));
  const setAu = (patch) => setSettings((s) => ({ ...s, automation: { ...s.automation, ...patch } }));
  const setRule = (patch) => setAu({ noLinkRule: { ...au.noLinkRule, ...patch } });
  const setDel = (patch) => setAu({ autoDeleteBanned: { ...au.autoDeleteBanned, ...patch } });
  const setFc = (patch) => setAu({ followerCheck: { ...au.followerCheck, ...patch } });
  const wn = au.warmupNew || DEFAULTS.automation.warmupNew;
  const wo = wn.options || DEFAULTS.automation.warmupNew.options;
  const setWn = (patch) => setAu({ warmupNew: { ...wn, ...patch } });
  const setWo = (patch) => setWn({ options: { ...wo, ...patch } });

  const vb = au.viralBoost || DEFAULTS.automation.viralBoost;
  const setVb = (next) => setAu({ viralBoost: next });
  const scanViralNow = async () => {
    try {
      await axios.post(`${API}/api/automation/viral-scan-now`);
      notify('Scan des posts viraux lancé — les comptes concernés seront boostés d\'ici ~1 min', 'success');
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur du scan', 'error');
    }
  };

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/automation/settings`);
      const s = data.settings || {};
      const savedAuto = s.automation || {};
      const merged = {
        telegram: { ...DEFAULTS.telegram, ...(s.telegram || {}) },
        automation: {
          ...DEFAULTS.automation,
          ...savedAuto,
          noLinkRule: { ...DEFAULTS.automation.noLinkRule, ...(savedAuto.noLinkRule || {}) },
          autoDeleteBanned: { ...DEFAULTS.automation.autoDeleteBanned, ...(savedAuto.autoDeleteBanned || {}) },
          followerCheck: savedAuto.followerCheck
            ? { ...DEFAULTS.automation.followerCheck, ...savedAuto.followerCheck }
            : { ...DEFAULTS.automation.followerCheck, times: [] },
          warmupNew: {
            ...DEFAULTS.automation.warmupNew,
            ...(savedAuto.warmupNew || {}),
            options: { ...DEFAULTS.automation.warmupNew.options, ...((savedAuto.warmupNew || {}).options || {}) },
          },
          viralBoost: { ...DEFAULTS.automation.viralBoost, ...(savedAuto.viralBoost || {}) },
        },
      };
      lastSaved.current = JSON.stringify(merged);
      setSettings(merged);
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  useEffect(() => {
    if (loading) return undefined;
    const cur = JSON.stringify(settings);
    if (cur === lastSaved.current) return undefined;
    setSaveState('saving');
    const id = setTimeout(async () => {
      try { await axios.post(`${API}/api/automation/settings`, settings); lastSaved.current = cur; setSaveState('saved'); }
      catch { setSaveState('error'); }
    }, 600);
    return () => clearTimeout(id);
  }, [settings, loading]);

  const testTelegram = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/api/automation/telegram/test`, { botToken: tg.botToken, chatId: tg.chatId });
      if (data.success) notify('Message de test envoyé — vérifie Telegram 📩');
      else notify(`Échec Telegram : ${data.error}`, 'error');
    } catch (e) { notify(e.response?.data?.error || 'Erreur du test', 'error'); }
    finally { setTesting(false); }
  };

  const fetchFlog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/automation/follower-log`);
      setFlog(data || { running: false, total: 0, done: 0, events: [] });
    } catch { /* silencieux */ }
  }, []);

  // Poll du journal live : rapide (3s) quand un run tourne, lent (15s) sinon.
  useEffect(() => {
    fetchFlog();
    const id = setInterval(fetchFlog, flog.running ? 3000 : 15000);
    return () => clearInterval(id);
  }, [fetchFlog, flog.running]);

  // ── Warm-up : comptes, programmations, logs 48h ────────────────────────────────
  const fetchWuAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sessions`);
      const list = (Array.isArray(data) ? data : []).map((s) => ({
        name: s.Name, username: (s.Data && s.Data.meta && s.Data.meta.username) || s.Name,
      }));
      setWuAccounts(list);
    } catch { /* noop */ }
  }, []);
  const fetchWuSchedules = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/sessions/warmup/schedules`); setWuSchedules(data.schedules || []); } catch { /* noop */ }
  }, []);
  const fetchWuHist = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/sessions/warmup/history`); setWuHist(data.entries || []); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetchWuAccounts(); fetchWuSchedules(); fetchWuHist();
    const id = setInterval(fetchWuHist, 20000); // logs 48h rafraîchis régulièrement
    return () => clearInterval(id);
  }, [fetchWuAccounts, fetchWuSchedules, fetchWuHist]);

  const scheduleWarmup = async (accounts, runAts, onDone) => {
    if (accounts.length === 0) { notify('Sélectionne au moins un compte.', 'warning'); return; }
    if (runAts.length === 0) { notify('Choisis au moins une date.', 'warning'); return; }
    setWuBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/sessions/warmup/schedule`, { accounts, options: wo, runAts });
      notify(`🗓️ ${data.count} warm-up(s) programmé(s).`, 'success');
      if (onDone) onDone();
      fetchWuSchedules();
    } catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
    finally { setWuBusy(false); }
  };
  const cancelWarmup = async (id) => {
    try { await axios.post(`${API}/api/sessions/warmup/unschedule`, { id }); setWuSchedules((p) => p.filter((s) => s.id !== id)); }
    catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/api/automation/check-now`);
      notify('Vérification des abonnés lancée ⏳');
      setTimeout(fetchFlog, 800); // rafraîchit le journal juste après le lancement
    }
    catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
    finally { setChecking(false); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}><CircularProgress /></Box>;
  }

  const tgReady = tg.enabled && tg.botToken && tg.chatId;
  const effectiveTimes = fc.mode === 'times' && fc.times.length === 0 ? [defaultHour] : fc.times;

  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1080, mx: 'auto', pb: 6 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <BoltIcon sx={{ color: t.primary }} />
            <Typography sx={{ fontSize: '1.55rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Automatisation</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.84rem', color: t.muted }}>
            Alertes, règles automatiques et planification — tout se sauvegarde tout seul.
          </Typography>
        </Box>
        <Box sx={{ px: 1.5, py: 0.75, borderRadius: 2, border: `1px solid ${t.border}`, bgcolor: t.card }}><SaveStatus state={saveState} /></Box>
      </Box>

      {/* ═══ Notifications ═══ */}
      <Section first>Notifications</Section>
      <Box sx={grid}>
        {/* Telegram */}
        <Card icon={<TelegramIcon fontSize="small" />} color="#38bdf8" title="Telegram"
          desc="Reçois les alertes directement sur Telegram."
          right={<>
            <Tag label={!tg.enabled ? 'Désactivé' : (tgReady ? 'Connecté' : 'À configurer')} color={!tg.enabled ? t.mutedFaint : (tgReady ? t.success : t.warning)} />
            <Switch checked={tg.enabled} onChange={(e) => setTg({ enabled: e.target.checked })} />
          </>}>
          <Typography sx={{ fontSize: '0.76rem', color: t.muted, my: 1.25 }}>
            Token via <b>@BotFather</b>, chat ID via <b>@userinfobot</b>.
          </Typography>
          <TextField fullWidth size="small" label="Bot API Token" value={tg.botToken}
            type={showToken ? 'text' : 'password'} placeholder="123456:ABC-DEF..."
            onChange={(e) => setTg({ botToken: e.target.value })} sx={{ mb: 1.25 }}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowToken((v) => !v)}>{showToken ? <EyeOffIcon sx={{ fontSize: 18 }} /> : <EyeIcon sx={{ fontSize: 18 }} />}</IconButton>
              </InputAdornment>
            ) }} />
          <TextField fullWidth size="small" label="Chat ID" value={tg.chatId} placeholder="123456789"
            onChange={(e) => setTg({ chatId: e.target.value })} sx={{ mb: 1.5 }} />
          <Button variant="outlined" size="small" startIcon={testing ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: 16 }} />}
            onClick={testTelegram} disabled={testing || !tg.botToken || !tg.chatId}>Message de test</Button>
        </Card>

        {/* Alertes */}
        <Card icon={<BellIcon fontSize="small" />} color="#f59e0b" title="Alertes"
          desc="Sois prévenu des événements importants."
          right={<Tag label={(au.notifyMilestones || au.notifySuspended) ? 'Actives' : 'Aucune'} color={(au.notifyMilestones || au.notifySuspended) ? t.success : t.mutedFaint} />}>
          <Row title="Paliers d'abonnés" desc="Alerte au franchissement d'un palier.">
            <Switch checked={au.notifyMilestones} onChange={(e) => setAu({ notifyMilestones: e.target.checked })} />
          </Row>
          {au.notifyMilestones && (
            <Box sx={{ py: 1.5 }}>
              <ChipEditor values={au.milestones} onChange={(v) => setAu({ milestones: [...v].sort((a, b) => a - b) })}
                placeholder="ex : 500" type="number" format={(v) => Number(v).toLocaleString('fr-FR')}
                parse={(v) => { const n = parseInt(v, 10); return Number.isFinite(n) && n > 0 ? n : ''; }} />
            </Box>
          )}
          <Row title="Compte suspendu" desc="Alerte quand un compte ne répond plus." last>
            <Switch checked={au.notifySuspended} onChange={(e) => setAu({ notifySuspended: e.target.checked })} />
          </Row>
        </Card>
      </Box>

      {/* ═══ Règles automatiques ═══ */}
      <Section>Règles automatiques</Section>
      <Box sx={grid}>
        {/* Règle pas de lien */}
        <Card icon={<LinkIcon fontSize="small" />} color={t.primary} title="Règle « pas de lien »"
          desc="Repère les comptes assez gros sans lien en bio."
          off={!rule.enabled}
          right={<>
            <Tag label={rule.enabled ? 'Active' : 'Désactivée'} color={rule.enabled ? t.success : t.mutedFaint} />
            <Switch checked={rule.enabled} onChange={(e) => setRule({ enabled: e.target.checked })} />
          </>}>
          <Row title="Seuil d'abonnés">
            <TextField size="small" type="number" value={rule.threshold}
              onChange={(e) => setRule({ threshold: parseInt(e.target.value, 10) || 0 })} sx={{ width: 110 }} />
          </Row>
          <Row title="Action">
            <ToggleButtonGroup exclusive size="small" value={rule.action} onChange={(_, v) => v && setRule({ action: v })}>
              <ToggleButton value="note">Note</ToggleButton>
              <ToggleButton value="tag">Tag</ToggleButton>
            </ToggleButtonGroup>
          </Row>
          <Row title="Prévenir sur Telegram" last>
            <Switch checked={rule.notify} onChange={(e) => setRule({ notify: e.target.checked })} />
          </Row>
          <Box sx={{ pt: 1.5 }}>
            {rule.action === 'note' ? (
              <TextField fullWidth size="small" label="Note à écrire" value={rule.note} onChange={(e) => setRule({ note: e.target.value })} />
            ) : (
              <TextField fullWidth size="small" label="Tag à ajouter" value={rule.tag} onChange={(e) => setRule({ tag: e.target.value })} />
            )}
          </Box>
        </Card>

        {/* Auto delete bannis */}
        <Card icon={<DeleteSweepIcon fontSize="small" />} color="#ef4444" title="Auto-delete comptes bannis"
          desc="Supprime un compte une fois sûr qu'il est banni."
          off={!del.enabled}
          right={<>
            <Tag label={del.enabled ? 'Actif' : 'Désactivé'} color={del.enabled ? '#f87171' : t.mutedFaint} />
            <Switch checked={del.enabled} onChange={(e) => setDel({ enabled: e.target.checked })} />
          </>}>
          <Typography sx={{ fontSize: '0.74rem', color: t.muted, my: 1 }}>
            Les <b>deux</b> conditions doivent être remplies (0 = condition ignorée).
          </Typography>
          <Row title="Après X vérifications" desc="Vérifs consécutives confirmant la suspension.">
            <TextField size="small" type="number" value={del.afterChecks}
              onChange={(e) => setDel({ afterChecks: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              sx={{ width: 90 }} inputProps={{ min: 0 }} />
          </Row>
          <Row title="Après X heures" desc="Délai depuis la 1ʳᵉ détection.">
            <TextField size="small" type="number" value={del.afterHours}
              onChange={(e) => setDel({ afterHours: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
              sx={{ width: 100 }} inputProps={{ min: 0 }} />
          </Row>
          <Row title="Prévenir sur Telegram" last>
            <Switch checked={del.notify} onChange={(e) => setDel({ notify: e.target.checked })} />
          </Row>
          {del.enabled && del.afterChecks <= 0 && del.afterHours <= 0 && (
            <Typography sx={{ fontSize: '0.74rem', color: '#fca5a5', fontWeight: 600, mt: 1.25 }}>
              ⚠️ Deux conditions à 0 → aucune suppression (sécurité).
            </Typography>
          )}
        </Card>
      </Box>

      {/* ═══ Planification ═══ */}
      <Section>Planification</Section>
      <Box sx={grid}>
        <Card span icon={<ScheduleIcon fontSize="small" />} color={t.success} title="Vérification des abonnés"
          desc="Quand rafraîchir automatiquement les abonnés de tes comptes."
          right={<Button size="small" variant="outlined" startIcon={checking ? <CircularProgress size={14} /> : <PlayIcon sx={{ fontSize: 16 }} />}
            onClick={checkNow} disabled={checking}>Vérifier maintenant</Button>}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap', pt: 1.5 }}>
            <ToggleButtonGroup exclusive size="small" value={fc.mode} onChange={(_, v) => v && setFc({ mode: v })}>
              <ToggleButton value="times">Heures fixes</ToggleButton>
              <ToggleButton value="interval">Intervalle</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flex: 1, minWidth: 260 }}>
              {fc.mode === 'times' ? (
                <Box>
                  <ChipEditor values={fc.times} onChange={(v) => setFc({ times: [...v].sort() })}
                    placeholder={defaultHour} type="time" color={t.success}
                    parse={(v) => (/^\d{1,2}:\d{2}$/.test(v) ? v.padStart(5, '0') : '')} />
                  <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint, mt: 1 }}>
                    {fc.times.length === 0
                      ? <>Aucune heure réglée → heure par défaut <b>{defaultHour}</b>.</>
                      : <>Vérifications à : {effectiveTimes.join(' · ')}</>}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: t.muted }}>Toutes les</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: t.success }}>{fc.intervalHours}h</Typography>
                  </Box>
                  <Slider value={fc.intervalHours} onChange={(_, v) => setFc({ intervalHours: v })} min={1} max={24} step={1} marks
                    sx={{ color: t.success }} />
                  <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>≈ {Math.floor(24 / fc.intervalHours)} vérification(s) par jour.</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Card>

        {/* Journal live de la vérification des abonnés */}
        <FollowerLog log={flog} onRefresh={fetchFlog} />
      </Box>

      {/* ═══ Warm-up automatique ═══ */}
      <Section>Warm-up automatique</Section>
      <Box sx={grid}>
        <Card span icon={<FireIcon fontSize="small" />} color="#f97316" title="Chauffer les nouveaux comptes"
          desc="Lance un warm-up automatiquement quelque temps après l'ajout d'un compte."
          off={!wn.enabled}
          right={<>
            <Tag label={wn.enabled ? 'Actif' : 'Désactivé'} color={wn.enabled ? '#fb923c' : t.mutedFaint} />
            <Switch checked={wn.enabled} onChange={(e) => setWn({ enabled: e.target.checked })} />
          </>}>
          <Box sx={{ pt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: 2,
              bgcolor: alpha('#f97316', 0.08), border: `1px solid ${alpha('#f97316', 0.25)}` }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#fdba74' }}>Chauffer un compte</Typography>
              <TextField size="small" type="number" value={wn.afterHours}
                onChange={(e) => setWn({ afterHours: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
                sx={{ width: 100 }} inputProps={{ min: 1, max: 720 }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#fdba74' }}>après son ajout.</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: 2,
              bgcolor: alpha('#f97316', 0.08), border: `1px solid ${alpha('#f97316', 0.25)}` }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#fdba74' }}>Puis le chauffer chaque jour pendant</Typography>
              <TextField size="small" type="number" value={wn.durationDays ?? 3}
                onChange={(e) => setWn({ durationDays: Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
                InputProps={{ endAdornment: <InputAdornment position="end">j</InputAdornment> }}
                sx={{ width: 100 }} inputProps={{ min: 1, max: 30 }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#fdba74' }}>(1 warm-up / jour).</Typography>
            </Box>

            <Typography sx={{ fontSize: '0.66rem', color: '#fdba74', mb: 1 }}>
              Les plages « min → max » sont tirées au hasard pour chaque compte (jamais deux pareils).
            </Typography>

            {/* rythme */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2.5, mb: 2.5 }}>
              <Range label="Durée / compte" unit=" min" min={1} max={60} value={[wo.durationMin, wo.durationMax]}
                onChange={(v) => setWo({ durationMin: v[0], durationMax: v[1] })} />
              <Range label="Délai entre actions" unit="s" min={2} max={60} value={[wo.minDelaySec, wo.maxDelaySec]}
                onChange={(v) => setWo({ minDelaySec: v[0], maxDelaySec: v[1] })} />
              <Single label="Comptes en parallèle" min={1} max={5} value={wo.concurrency} onChange={(v) => setWo({ concurrency: v })} />
            </Box>

            {/* actions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* like */}
              <Box sx={{ borderRadius: 2, border: `1px solid ${wo.like ? alpha('#f472b6', 0.4) : t.border}`, bgcolor: wo.like ? alpha('#f472b6', 0.05) : t.input, p: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LikeIcon sx={{ fontSize: 17, color: '#f472b6' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, flex: 1 }}>Liker des posts</Typography>
                  <Switch size="small" checked={wo.like} onChange={(e) => setWo({ like: e.target.checked })} />
                </Box>
                {wo.like && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1.25 }}>
                    <Single label="% de posts likés" unit="%" min={1} max={100} color="#f472b6" value={wo.likeProb} onChange={(v) => setWo({ likeProb: v })} />
                    <Range label="Max likes / compte" min={1} max={40} color="#f472b6" value={[wo.maxLikes, wo.maxLikesHi]}
                      onChange={(v) => setWo({ maxLikes: v[0], maxLikesHi: v[1] })} />
                  </Box>
                )}
              </Box>
              {/* view */}
              <Box sx={{ borderRadius: 2, border: `1px solid ${wo.view ? alpha('#93c5fd', 0.4) : t.border}`, bgcolor: wo.view ? alpha('#93c5fd', 0.05) : t.input, p: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ViewIcon sx={{ fontSize: 17, color: '#93c5fd' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, flex: 1 }}>Ouvrir des posts</Typography>
                  <Switch size="small" checked={wo.view} onChange={(e) => setWo({ view: e.target.checked })} />
                </Box>
                {wo.view && (
                  <Box sx={{ mt: 1.25, width: { xs: '100%', sm: '50%' } }}>
                    <Single label="% de posts ouverts" unit="%" min={1} max={100} color="#93c5fd" value={wo.viewProb} onChange={(v) => setWo({ viewProb: v })} />
                  </Box>
                )}
              </Box>
              {/* comment */}
              <Box sx={{ borderRadius: 2, border: `1px solid ${wo.comment ? alpha('#c4b5fd', 0.4) : t.border}`, bgcolor: wo.comment ? alpha('#c4b5fd', 0.05) : t.input, p: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReplyIcon sx={{ fontSize: 17, color: '#c4b5fd' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, flex: 1 }}>Commenter</Typography>
                  <Switch size="small" checked={wo.comment} onChange={(e) => setWo({ comment: e.target.checked })} />
                </Box>
                {wo.comment && (
                  <Box sx={{ mt: 1 }}>
                    <TextField size="small" fullWidth multiline minRows={2} label="Commentaires possibles (un par ligne)"
                      value={(wo.comments || []).join('\n')}
                      onChange={(e) => setWo({ comments: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })}
                      placeholder={'trop bien 🔥\nj\'adore'} sx={{ mb: 1 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Single label="% de posts commentés" unit="%" min={1} max={100} color="#c4b5fd" value={wo.commentProb} onChange={(v) => setWo({ commentProb: v })} />
                      <Range label="Max commentaires / compte" min={1} max={20} color="#c4b5fd" value={[wo.maxComments, wo.maxCommentsHi]}
                        onChange={(v) => setWo({ maxComments: v[0], maxCommentsHi: v[1] })} />
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#fca5a5', mt: 0.75 }}>⚠️ Action la plus risquée : garde un max bas.</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Card>

        <WarmupScheduler accounts={wuAccounts} schedules={wuSchedules} onSchedule={scheduleWarmup} onCancel={cancelWarmup} busy={wuBusy} />
        <WarmupLog entries={wuHist} onRefresh={fetchWuHist} />
      </Box>

      {/* ═══ Viral Boost ═══ */}
      <Section>Republier les posts viraux</Section>
      <Box sx={grid}>
        <ViralBoostCard cfg={vb} onChange={setVb} onScanNow={scanViralNow} />
      </Box>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.sev} onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
